import React, { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { makeStyles } from 'tss-react/mui';
import { CloseButton } from '../buttons';
import { PageSection, PageSectionProps } from '../pageSection';

const useStyles = makeStyles({ name: 'InPageDialog' })(theme => ({
	paddingWrapper: {
		paddingTop: 40,
		paddingBottom: 40
	},
	maxWidthWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch'
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: 20
	},
	title: {
		marginRight: 10,
		fontSize: 28,
		fontWeight: 'bold',
		letterSpacing: 0.25,
		[theme.breakpoints.up('md')]: {
			fontSize: 42
		}
	},
	closeButton: {
		marginLeft: 'auto',
		alignSelf: 'flex-start',
		flexShrink: 0
	}
}));

export type InPageDialogClasses = {
	head?: string;
	title?: string;
	closeButton?: string;
};

export type InPageDialogProps = PropsWithChildren<{
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	classes?: InPageDialogClasses;
	pageSectionProps?: PageSectionProps;
}>;

export const InPageDialog: React.FC<InPageDialogProps> = (props) => {
	const { isOpen, children, onClose, title, pageSectionProps } = props;

	let { classes: styles, cx } = useStyles(undefined, {props});

	if (!isOpen)
		return null;

	return (
		<InPageDialogBase>
			<PageSection {...pageSectionProps} classes={{
				paddingWrapper: cx(styles.paddingWrapper, pageSectionProps?.classes?.paddingWrapper),
				maxWidthWrapper: cx(styles.maxWidthWrapper, pageSectionProps?.classes?.maxWidthWrapper)
			}}>
				<div className={styles.head}>
					{title && title.length &&
						<span className={styles.title}>{title}</span>}
					<CloseButton className={styles.closeButton} onClick={onClose} />
				</div>
				{children}
			</PageSection>
		</InPageDialogBase>
	);
};

type InPageDialogContextProps = {
	ref: React.MutableRefObject<HTMLDivElement>,
	open: () => void;
	close: () => void;
};

const InPageDialogContext = createContext<InPageDialogContextProps>({
	ref: null,
	open: () => { },
	close: () => { }
});

export const InPageDialogProvider = (props: PropsWithChildren) => {
	const ref = useRef<HTMLDivElement>();
	const [isOpen, setIsOpen] = useState(false);
	const inPageDialogContextValue = ({
		ref: ref,
		open: () => setIsOpen(true),
		close: () => setIsOpen(false)
	});
	return (
		<InPageDialogContext.Provider value={inPageDialogContextValue}>
			<div ref={ref} />
			<div {...props} style={{ display: isOpen ? 'none' : null }} />
		</InPageDialogContext.Provider>
	);
};

const InPageDialogBase = (props: PropsWithChildren) => {
	const { children } = props;
	const { ref, open, close } = useContext(InPageDialogContext);

	useEffect(() => {
		open();
		return () => close();
	}, [open, close]);

	return ReactDOM.createPortal(
		children, ref.current
	);
};
